import { FileList } from "./components/FileList";
import { useFileUploadState } from "./hooks/useFileUploadState";
import { UploadedFile } from "./types/UploadedFile.type";
import { DropzoneInput } from "./components/DropZoneInput";
import { UploadStatus } from "./types/UploadStatus.type";
import { FileUploadProps, FileUploadRef } from "./types/FileUpload.types";
import { showToast } from "utils/helpers";
import { DEFAULT_ACCEPTED_FILE_TYPES } from "./constants/acceptedFileTypes";
import { useHasFileUploadContext } from "./hooks/FileUploadContext";
import { convertFileAbleFileToUploadedFile, deleteFileUpload } from "./utils";
import { UploadFile } from "types/storage.types";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { safeRandomUUID } from "newui/utils/general";

const maxFileNameLength = 200;

/**
 * Provides FileUpload capability with in-built drag+drop zone.
 * @param param0
 * @returns
 */
export const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(
	(
		{
			allowFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
			description = null,
			disabled,
			extraData,
			isPreview,
			maxFileSizeMB = 100,
			multiple = false,
			required,
			supportsHardDelete,
			uploadType,
			initialValue,
			onChange,
		},
		ref
	) => {
		const hasContext = useHasFileUploadContext();
		const bottomRef = useRef<HTMLDivElement | null>(null);

		const mappedInitialFiles = useMemo(
			() =>
				(initialValue ?? []).map((file) => convertFileAbleFileToUploadedFile(file)),
			[initialValue]
		);

		const { files, addFiles, clearFiles, removeFile, replaceFiles, updateUploadedFile } =
			useFileUploadState({
				multiple,
				onChange,
				files: mappedInitialFiles,
			});

		useImperativeHandle(ref, () => ({
			clear: clearFiles,
			setFiles: replaceFiles,
		}), [clearFiles, replaceFiles]);

		// Scroll to bottom when files change
		useEffect(() => {
			if (files.length > 0) {
				bottomRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "end",
				});
			}
		}, [files.length]);

		if (!hasContext) {
			console.error(
				"FileUpload must be used within a FileUploadProvider. " +
				"Wrap your component with <FileUploadProvider>."
			);
			return (
				<div className="rounded-lg border-2 border-red-500 bg-red-50 p-4">
					<p className="font-semibold text-red-700">Configuration Error</p>
					<p className="text-sm text-red-600">
						FileUpload requires FileUploadProvider. Please wrap your component
						with it.
					</p>
				</div>
			);
		}

		const onFileRemoved = async (id: number | string) => {
			if (supportsHardDelete) {
				const fileToDelete = files.find((f) => {
					if (typeof id === "string") {
						return f.uploadId === id;
					}
					return f.id === id;
				});

				if (fileToDelete?.id && fileToDelete?.path) {
					// Show deleting state while API call is in progress
					if (fileToDelete.uploadId) {
						updateUploadedFile(fileToDelete.uploadId, {
							...fileToDelete,
							status: UploadStatus.DELETING,
						});
					}

					const uploadFile: UploadFile = {
						id: fileToDelete.id,
						file_name: fileToDelete.name,
						file_path: fileToDelete.path,
						file_size: fileToDelete.size ?? 0,
					};

					const result = await deleteFileUpload(uploadFile, uploadType);

					if (!result.success) {
						// Revert deleting state on failure
						if (fileToDelete.uploadId) {
							updateUploadedFile(fileToDelete.uploadId, {
								...fileToDelete,
								status: UploadStatus.UPLOADED,
							});
						}
						showToast("Failed to delete file. Please try again.", "Error");
						return;
					}
				}
			}

			removeFile(id);
		};

		const handleFilesAccepted = (rawFiles: File[]) => {
			const isCorrectFileSize = (file: File) => {
				if (!maxFileSizeMB) {
					return true;
				}

				const isValid =
					maxFileSizeMB && file.size <= maxFileSizeMB * 1024 * 1024;

				if (!isValid) {
					showToast(
						`Maximum file size is ${maxFileSizeMB}MB. File '${file.name}' is larger than this.`,
						"Error"
					);
				}

				return isValid;
			};

			const isCorrectFileNameLength = (fileName: string) => {
				const isValid = fileName.length <= maxFileNameLength;

				if (!isValid) {
					showToast(
						`Maximum file name length is ${maxFileNameLength} characters (including the extension). The file '${fileName}' has ${fileName.length} characters.`,
						"Error"
					);
				}

				return isValid;
			};

			const acceptedFiles: UploadedFile[] = rawFiles.flatMap((file) => {
				if (isCorrectFileNameLength(file.name) && isCorrectFileSize(file)) {
					return [
						{
							uploadId: safeRandomUUID(),
							name: file.name,
							size: file.size,
							raw: file,
							status: UploadStatus.IDLE,
						},
					];
				} else {
					return [];
				}
			});

			if (!multiple && acceptedFiles.length > 1) {
				showToast("Please upload just one file", "Error");
				return;
			}

			addFiles(acceptedFiles);
		};

		const passThruExtraData = { ...extraData, model: uploadType };

		const uploadComplete = (
			originalFile: UploadedFile,
			serverData: UploadedFile
		) => {
			updateUploadedFile(originalFile.uploadId, serverData);
		};

		// A disabled FileUpload should behave like preview mode: no dropzone, no delete
		// button on existing files. Callers can still pass `isPreview` explicitly for
		// read-only-but-not-disabled cases. Either flag flips the control to read-only.
		const effectivePreview = isPreview || disabled;

		return (
			<div>
				{!effectivePreview && (
					<DropzoneInput
						disabled={disabled}
						maxFileSizeMB={maxFileSizeMB}
						multiple={multiple}
						accept={allowFileTypes}
						onFilesAccepted={handleFilesAccepted}
						description={description}
					/>
				)}

				<FileList
					files={files}
					isPreview={effectivePreview}
					extraData={passThruExtraData}
					onRemove={onFileRemoved}
					onUploadComplete={uploadComplete}
				/>

				<div ref={bottomRef} />
			</div>
		);
	}
);

FileUpload.displayName = "FileUpload";